package org.teddapp.project.db.repo

import app.cash.sqldelight.coroutines.asFlow
import app.cash.sqldelight.coroutines.mapToList
import com.tedd.db.Portfolio_piece
import com.tedd.db.Portfolio_piece_competence
import com.tedd.db.TeddDatabase
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.withContext
import org.teddapp.project.db.newId
import org.teddapp.project.db.nowMillis
import org.teddapp.project.portfolio.PieceImage
import org.teddapp.project.portfolio.PieceLink
import org.teddapp.project.portfolio.PieceStatus
import org.teddapp.project.portfolio.PortfolioCodecs

/** Une compétence (et éventuellement un critère précis) qu'une pièce démontre. */
data class PieceCompetenceRef(val competenceId: String, val criterionId: String? = null, val note: String? = null)

/**
 * Portfolio : pièces racontées à la main et liaisons vers les compétences démontrées.
 * La liaison est une ligne identifiée ; on la restaure plutôt que d'en créer une nouvelle
 * pour garder le même id entre appareils.
 */
class PortfolioRepository(private val db: TeddDatabase) {

    fun observePieces(): Flow<List<Portfolio_piece>> =
        db.portfolioPieceQueries.selectAll().asFlow().mapToList(Dispatchers.Default)

    fun observeLinks(): Flow<List<Portfolio_piece_competence>> =
        db.portfolioPieceCompetenceQueries.selectAll().asFlow().mapToList(Dispatchers.Default)

    suspend fun create(
        title: String,
        subtitle: String? = null,
        body: String = "",
        links: List<PieceLink> = emptyList(),
        images: List<PieceImage> = emptyList(),
        dateStart: String? = null,
        dateEnd: String? = null,
        projectId: String? = null,
        stepId: String? = null,
        status: String = PieceStatus.DRAFT,
        competences: List<PieceCompetenceRef> = emptyList(),
        id: String = newId("pp"),
    ): String = withContext(Dispatchers.Default) {
        val now = nowMillis()
        val next = (db.portfolioPieceQueries.selectAll().executeAsList().maxOfOrNull { it.order_index } ?: -1L) + 1L
        db.transaction {
            db.portfolioPieceQueries.insert(
                id, title.trim(), subtitle.clean(), body, PortfolioCodecs.encodeLinks(links), PortfolioCodecs.encodeImages(images),
                dateStart.clean(), dateEnd.clean(), projectId.clean(), stepId.clean(), status, 0L, next, now, now,
            )
            applyCompetences(id, competences, now)
        }
        id
    }

    suspend fun update(
        id: String,
        title: String,
        subtitle: String?,
        body: String,
        links: List<PieceLink>,
        images: List<PieceImage>,
        dateStart: String?,
        dateEnd: String?,
        projectId: String?,
        stepId: String?,
        status: String,
        competences: List<PieceCompetenceRef>,
    ) = withContext(Dispatchers.Default) {
        val existing = db.portfolioPieceQueries.selectById(id).executeAsOneOrNull() ?: return@withContext
        val now = nowMillis()
        db.transaction {
            db.portfolioPieceQueries.update(
                title.trim(), subtitle.clean(), body, PortfolioCodecs.encodeLinks(links), PortfolioCodecs.encodeImages(images),
                dateStart.clean(), dateEnd.clean(), projectId.clean(), stepId.clean(), status, existing.pinned, existing.order_index, now, id,
            )
            applyCompetences(id, competences, now)
        }
    }

    suspend fun setStatus(id: String, status: String) = withContext(Dispatchers.Default) {
        db.portfolioPieceQueries.setStatus(status, nowMillis(), id)
    }

    suspend fun setPinned(id: String, pinned: Boolean) = withContext(Dispatchers.Default) {
        db.portfolioPieceQueries.setPinned(if (pinned) 1L else 0L, nowMillis(), id)
    }

    suspend fun reorder(orderedIds: List<String>) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.transaction {
            orderedIds.forEachIndexed { i, id -> db.portfolioPieceQueries.reorder(i.toLong(), now, id) }
        }
    }

    suspend fun softDelete(id: String) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.transaction {
            db.portfolioPieceCompetenceQueries.selectByPiece(id).executeAsList()
                .forEach { db.portfolioPieceCompetenceQueries.softDelete(now, now, it.id) }
            db.portfolioPieceQueries.softDelete(now, now, id)
        }
    }

    /** Remplace les compétences démontrées par différence : restaure, insère, soft-supprime. */
    suspend fun setCompetences(pieceId: String, wanted: List<PieceCompetenceRef>) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.transaction { applyCompetences(pieceId, wanted, now) }
    }

    private fun applyCompetences(pieceId: String, wanted: List<PieceCompetenceRef>, now: Long) {
        val all = db.portfolioPieceCompetenceQueries.selectByPieceAnyState(pieceId).executeAsList()
        val keyOf = { competenceId: String, criterionId: String? -> competenceId to criterionId }
        val wantedKeys = wanted.map { keyOf(it.competenceId, it.criterionId) }.toSet()
        val seen = mutableSetOf<Pair<String, String?>>()

        all.forEach { row ->
            val key = keyOf(row.competence_id, row.criterion_id)
            val isWanted = key in wantedKeys
            when {
                isWanted && key in seen -> if (row.is_deleted == 0L) db.portfolioPieceCompetenceQueries.softDelete(now, now, row.id)
                isWanted -> {
                    seen += key
                    val ref = wanted.first { keyOf(it.competenceId, it.criterionId) == key }
                    val position = wanted.indexOf(ref).toLong()
                    if (row.is_deleted == 1L) db.portfolioPieceCompetenceQueries.restore(now, row.id)
                    if (row.note != ref.note.clean() || row.order_index != position) {
                        db.portfolioPieceCompetenceQueries.update(pieceId, ref.competenceId, ref.criterionId, ref.note.clean(), position, now, row.id)
                    }
                }
                row.is_deleted == 0L -> db.portfolioPieceCompetenceQueries.softDelete(now, now, row.id)
            }
        }
        wanted.forEachIndexed { i, ref ->
            val key = keyOf(ref.competenceId, ref.criterionId)
            if (key !in seen) {
                seen += key
                db.portfolioPieceCompetenceQueries.insert(
                    newId("ppc"), pieceId, ref.competenceId, ref.criterionId, ref.note.clean(), i.toLong(), now, now,
                )
            }
        }
    }

    private fun String?.clean(): String? = this?.trim()?.takeIf { it.isNotBlank() }
}
